import { ChangeEvent, useState } from 'react'

import Button from 'common/components/button'
import FormItem from 'common/components/FormItem'
import Modal from 'common/components/Modal'
import Textarea from 'common/components/textarea'
import { errorToast } from 'common/components/toastNotification'
import { Replacer } from 'common/components/topics/interfaces'
import { useAppSelector } from 'common/hooks/redux'
import { useBulkCreatePhraseMutation } from 'features/content/api/mutations'
import useGetReplacersQuery from 'features/content/queries/useGetConfiguredReplacersQuery'

import { validReplacerRegex } from './cells/PhraseCell'

const MAX_LINE_LENGTH = 60

type Props = {
  isOpen: boolean
  selectedNodeId: string
  close: () => void
}

const AddPhrasesModal = ({ isOpen, selectedNodeId, close }: Props) => {
  const [phrases, setPhrases] = useState<string>()
  const [warning, setWarning] = useState<string>()

  const userId = useAppSelector((state) => state.authStates.user_id)
  const createPhrasesBulkMutation = useBulkCreatePhraseMutation(selectedNodeId)

  const { replacers } = useGetReplacersQuery()

  const handleOnChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setPhrases(value)
    const showMaxLineWarning = value
      .split('\n')
      .some((line) => line.length > MAX_LINE_LENGTH)
    setWarning(
      showMaxLineWarning
        ? 'Please make sure all phrases are separated by line breaks'
        : undefined
    )
  }

  const onClose = () => {
    setPhrases(undefined)
    setWarning(undefined)
    close()
  }

  const handleAddPhrases = async () => {
    const errorList: string[] = []

    const phrasesList = phrases?.split('\n').filter((phrase) => phrase !== '')
    const successPhrasesList: string[] = []

    let hasInvalidReplacer = false
    phrasesList?.forEach((phrase) => {
      const replacerMatches = phrase?.match(validReplacerRegex)
      if (replacerMatches) {
        replacerMatches.forEach((match) => {
          const foundReplacer = replacers?.find(
            (replacer) => (replacer.original as Replacer).replacer === match
          )
          if (foundReplacer) {
            successPhrasesList.push(phrase)
          } else {
            errorList.push(phrase)
            hasInvalidReplacer = true
          }
        })
      } else {
        successPhrasesList.push(phrase)
      }
    })

    if (hasInvalidReplacer) {
      setWarning('Please make sure that phrases contain valid replacers.')
    }

    if (successPhrasesList && successPhrasesList.length > 0) {
      createPhrasesBulkMutation.mutate(
        {
          userId,
          phrases: successPhrasesList?.map((phrase) => ({
            frequency: '1.0',
            phrase,
          })),
          nodeId: Number(selectedNodeId),
          replacers,
        },
        {
          onSuccess: (result) => {
            result.failedPhrases.forEach((phrase) => {
              errorList.push(phrase.phrase)
              setWarning('Failed to add these phrases.')
            })

            if (errorList.length > 0) {
              errorToast(`Failed to add the following phrases.`)
              setPhrases(errorList.join('\n'))
            } else {
              onClose()
            }
          },
        }
      )
    }
  }

  return (
    <Modal
      visible={isOpen}
      title="Add multiple phrases"
      isResponsive
      closable
      centered
      style={{ maxWidth: '1300px' }}
      destroyOnClose={true}
      onClose={onClose}
      maskClosable={false}
      footer={
        <div className="flex justify-end">
          <Button
            variant="primary"
            data-cy="bulk-add-phrases-button"
            onClick={handleAddPhrases}
            disabled={!phrases}
            loading={createPhrasesBulkMutation.isLoading}
          >
            Add phrases
          </Button>
        </div>
      }
    >
      <div className="text-coolGray-400 mb-4">
        Bulk add phrases to your selected node. Type or paste in the phrases you
        want to add, separated by line breaks.
      </div>
      <FormItem error={warning}>
        <Textarea
          allowTab
          className="h-75 resize-none bg-coolGray-100"
          autoFocus
          value={phrases}
          onChange={handleOnChange}
          placeholder="Add phrases..."
          data-cy="bulk-add-phrases-textarea"
        />
      </FormItem>
    </Modal>
  )
}

export default AddPhrasesModal
